import { Link } from "@tanstack/react-router";

/**
 * The image panel in the products hero, on /products and /products/<category>.
 *
 * It replaces AnimatedCatalogHeader, a decorative SVG that linked nowhere and
 * carried four claims the project had already banned — "HACCP Certified" and
 * "100% Quality Guaranteed" (VARS-029 removed both from the locale files but
 * missed this file, because they were hardcoded in JSX), plus "10+ Air
 * Corridors" and three named fixed routes, which ADR-010 forbids for a
 * multi-origin business.
 *
 * So the panel is now real product photography that goes somewhere. A buyer
 * landing on the catalogue can enter a category from the hero instead of
 * scrolling past a diagram.
 *
 * Every label comes from an existing locale key — `cat.<slug>` and
 * `product.<slug>.title` — so this adds no translation debt and cannot coin a
 * product name (AGENTS.md §9, enforced by product-names.test.ts).
 */
export type MosaicTile =
  | { kind: "category"; slug: string; label: string; image?: string }
  | { kind: "product"; category: string; slug: string; label: string; image?: string };

const TILE_CLASS =
  "group relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 bg-white/5 " +
  "transition-colors hover:border-mint/60 focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-navy";

/**
 * `alt=""` is deliberate: the visible label sits inside the same link, so it
 * already supplies the accessible name. Giving the image its own alt would
 * make a screen reader announce the destination twice.
 *
 * object-contain, not object-cover — three of the four category images are
 * packshots, and cover crops straight through the product. Same treatment as
 * ProductCard, which keeps the hero agreeing with the grid beneath it.
 */
function TileBody({ label, image, eager }: { label: string; image?: string; eager: boolean }) {
  return (
    <>
      {image ? (
        <img
          src={image}
          alt=""
          loading={eager ? "eager" : "lazy"}
          fetchPriority={eager ? "high" : "auto"}
          decoding="async"
          width={400}
          height={300}
          className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="flex h-full items-center justify-center font-display text-3xl font-bold text-white/15">
          {label.slice(0, 2)}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy via-navy/80 to-transparent p-3 pt-8">
        <span className="font-display text-xs font-bold leading-snug text-white line-clamp-2">
          {label}
        </span>
      </div>
    </>
  );
}

export function CategoryMosaic({ tiles }: { tiles: MosaicTile[] }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {tiles.map((tile, i) =>
        tile.kind === "category" ? (
          <Link
            key={tile.slug}
            to="/products/$category"
            params={{ category: tile.slug }}
            className={TILE_CLASS}
          >
            <TileBody label={tile.label} image={tile.image} eager={i === 0} />
          </Link>
        ) : (
          <Link
            key={tile.slug}
            to="/products/$category/$slug"
            params={{ category: tile.category, slug: tile.slug }}
            className={TILE_CLASS}
          >
            <TileBody label={tile.label} image={tile.image} eager={i === 0} />
          </Link>
        ),
      )}
    </div>
  );
}
